import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";

export type BitrateHistoryRange = "5m" | "15m" | "1h" | "3h" | "6h" | "12h" | "24h" | "all";

type StreamBitrateAnalyticsChartProps = {
  streamId: string;
  range: BitrateHistoryRange;
  refreshMs: number;
};

type AnalyticsBitratePoint = {
  timestamp?: string;
  avgMbps?: number;
  minMbps?: number;
  maxMbps?: number;
  samples?: number;
};

type AnalyticsBitrateResponse = {
  streamId?: string;
  bucket?: string;
  points?: AnalyticsBitratePoint[];
  error?: string;
  details?: string;
};

type ChartPoint = {
  time: string;
  avg: number;
  min: number;
  max: number;
  samples: number;
};

const RANGE_TO_DURATION_MS: Record<BitrateHistoryRange, number | null> = {
  "5m": 5 * 60 * 1000,
  "15m": 15 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "3h": 3 * 60 * 60 * 1000,
  "6h": 6 * 60 * 60 * 1000,
  "12h": 12 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  all: null,
};
const MAX_CHART_POINTS = 300;
const pickBucketForRange = (range: BitrateHistoryRange) => {
  switch (range) {
    case "5m":
    case "15m":
      return "raw";
    case "1h":
    case "3h":
      return "5s";
    case "6h":
    case "12h":
    case "24h":
    case "all":
    default:
      return "1m";
  }
};

const normalizePoint = (point: AnalyticsBitratePoint) => {
  const parsed = {
    avg: typeof point.avgMbps === "number" && Number.isFinite(point.avgMbps) ? point.avgMbps : 0,
    min: typeof point.minMbps === "number" && Number.isFinite(point.minMbps) ? point.minMbps : 0,
    max: typeof point.maxMbps === "number" && Number.isFinite(point.maxMbps) ? point.maxMbps : 0,
    samples: typeof point.samples === "number" && Number.isFinite(point.samples) ? point.samples : 0,
  };
  const timestamp = point.timestamp ? new Date(point.timestamp) : null;
  return { ...parsed, timestamp };
};

const StreamBitrateAnalyticsChart = ({ streamId, range, refreshMs }: StreamBitrateAnalyticsChartProps) => {
  const { authorizedFetch } = useAuth();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const { width, height } = entry.contentRect;
      setContainerSize((current) =>
        current.width === width && current.height === height ? current : { width, height },
      );
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const bitrateQuery = useQuery<AnalyticsBitrateResponse, Error>({
    queryKey: ["analytics-stream-bitrates", streamId, range],
    enabled: Boolean(streamId),
    refetchInterval: refreshMs,
    refetchIntervalInBackground: true,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const end = new Date();
      const duration = RANGE_TO_DURATION_MS[range];
      const start = duration === null ? new Date(0) : new Date(end.getTime() - duration);
      const params = new URLSearchParams();
      params.set("end", end.toISOString());
      params.set("start", start.toISOString());
      params.set("bucket", pickBucketForRange(range));
      const response = await authorizedFetch(
        `/api/analytics/streams/${encodeURIComponent(streamId)}/bitrates?${params.toString()}`,
      );
      const payload = (await response.json().catch(() => null)) as AnalyticsBitrateResponse | null;
      if (!response.ok) {
        const baseError = payload?.error ?? "Unable to load analytics bitrate data";
        const details = typeof payload?.details === "string" ? payload.details.trim() : "";
        throw new Error(details ? `${baseError}: ${details}` : baseError);
      }
      return payload ?? {};
    },
  });

  const chartData: ChartPoint[] = useMemo(() => {
    const bucket = bitrateQuery.data?.bucket ?? "raw";
    const bucketDurationMs = bucket === "1m" ? 60_000 : bucket === "5s" ? 5_000 : 1_000;
    const duration = RANGE_TO_DURATION_MS[range];
    const nowMs = Date.now();

    const normalizedPoints = (bitrateQuery.data?.points ?? [])
      .map(normalizePoint)
      .filter((point) => point.timestamp instanceof Date && !Number.isNaN(point.timestamp.valueOf()))
      .sort((a, b) => (a.timestamp!.getTime() > b.timestamp!.getTime() ? 1 : -1));

    if (normalizedPoints.length === 0) {
      return [];
    }

    const windowEnd = duration === null ? normalizedPoints[normalizedPoints.length - 1].timestamp!.getTime() : nowMs;
    const windowStart = duration === null ? normalizedPoints[0].timestamp!.getTime() : windowEnd - duration;

    const filteredPoints = normalizedPoints.filter((point) => {
      const ts = point.timestamp!.getTime();
      return ts >= windowStart && ts <= windowEnd;
    });

    if (filteredPoints.length === 0) {
      return [];
    }

    const showDate = duration === null || duration >= 12 * 60 * 60 * 1000;
    const formatter = new Intl.DateTimeFormat(undefined, {
      hour12: false,
      ...(showDate ? { month: "short", day: "numeric" } : {}),
      hour: "2-digit",
      minute: "2-digit",
    });

    const startMs = Math.floor(windowStart / bucketDurationMs) * bucketDurationMs;
    const endMs = Math.max(windowEnd, filteredPoints[filteredPoints.length - 1].timestamp!.getTime());

    const bucketMap = new Map<number, { avg: number; min: number; max: number; samples: number }>();
    filteredPoints.forEach((point) => {
      const bucketKey = Math.floor(point.timestamp!.getTime() / bucketDurationMs) * bucketDurationMs;
      bucketMap.set(bucketKey, {
        avg: point.avg,
        min: point.min,
        max: point.max,
        samples: point.samples,
      });
    });

    const data: ChartPoint[] = [];
    for (let ts = startMs; ts <= endMs; ts += bucketDurationMs) {
      const stored = bucketMap.get(ts);
      const value = stored ?? { avg: 0, min: 0, max: 0, samples: 0 };
      data.push({
        time: formatter.format(new Date(ts)),
        avg: value.avg,
        min: value.min,
        max: value.max,
        samples: value.samples,
      });
    }
    if (data.length <= MAX_CHART_POINTS) {
      return data;
    }
    const groupSize = Math.ceil(data.length / MAX_CHART_POINTS);
    const aggregated: ChartPoint[] = [];
    for (let i = 0; i < data.length; i += groupSize) {
      const slice = data.slice(i, i + groupSize);
      const samples = slice.reduce((sum, point) => sum + point.samples, 0);
      if (samples === 0) {
        const last = slice[slice.length - 1];
        aggregated.push({
          time: last.time,
          avg: 0,
          min: 0,
          max: 0,
          samples: 0,
        });
        continue;
      }
      const avg =
        slice.reduce((sum, point) => sum + point.avg * point.samples, 0) /
        Math.max(1, samples);
      const min = slice.reduce((value, point) => Math.min(value, point.min), Number.POSITIVE_INFINITY);
      const max = slice.reduce((value, point) => Math.max(value, point.max), 0);
      const last = slice[slice.length - 1];
      aggregated.push({
        time: last.time,
        avg,
        min: Number.isFinite(min) ? min : 0,
        max,
        samples,
      });
    }
    return aggregated;
  }, [bitrateQuery.data?.bucket, bitrateQuery.data?.points, range]);

  const maxValue = useMemo(() => {
    if (!chartData.length) {
      return 1;
    }
    const maximumAvg = chartData.reduce((max, point) => (point.avg > max ? point.avg : max), 0);
    return maximumAvg > 0 ? maximumAvg * 1.1 : 1;
  }, [chartData]);

  const chartBackgroundColor = theme.palette.background.paper;
  const chartBorderColor = theme.palette.divider;
  const isCompact = useMemo(
    () => containerSize.width < 320 || containerSize.height < 120,
    [containerSize.height, containerSize.width],
  );

  const renderTooltip = useCallback(
    (props: TooltipProps<any, any>) => {
      const { active, payload, label } = props as any;
      if (!active || !payload || payload.length === 0) {
        return null;
      }
      const point = payload[0]?.payload as ChartPoint | undefined;
      if (!point) {
        return null;
      }
      return (
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 3,
            p: 1,
            border: `1px solid ${theme.palette.divider}`,
            minWidth: 160,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Avg: {point.avg.toFixed(2)} Mbit/s
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Min: {point.min.toFixed(2)} Mbit/s
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Max: {point.max.toFixed(2)} Mbit/s
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Samples: {point.samples}
          </Typography>
        </Box>
      );
    },
    [theme],
  );

  if (bitrateQuery.isPending && !bitrateQuery.data) {
    return (
      <Box
        ref={containerRef}
        sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: chartBackgroundColor }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (bitrateQuery.isError) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: chartBackgroundColor,
          border: `1px solid ${chartBorderColor}`,
          borderRadius: 2,
          px: 2,
        }}
      >
        <Typography variant="body2" color="error">
          {(bitrateQuery.error as Error)?.message ?? "Unable to load analytics bitrate data"}
        </Typography>
      </Box>
    );
  }

  if (!chartData.length) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: chartBackgroundColor,
          border: `1px solid ${chartBorderColor}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No bitrate data for this range.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: chartBackgroundColor,
        border: `1px solid ${chartBorderColor}`,
        borderRadius: 2,
        pt: 1,
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={
            isCompact
              ? { top: 8, right: 6, left: -20, bottom: 0 }
              : { top: 10, right: 16, left: -20, bottom: 6 }
          }
        >
          <CartesianGrid
            strokeDasharray="3 3"
            strokeOpacity={0.5}
            stroke={theme.palette.divider}
          />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
            label={{ fontSize: 10 }}
          />
          <YAxis
            dataKey="avg"
            domain={[0, maxValue]}
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            label={
              isCompact
                ? undefined
                : { value: "Mbit/s", angle: -90, position: "insideLeft", fontSize: 10, offset: 10 }
            }
          />
          <defs>
            <linearGradient id="analyticsBitrateGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.45} />
              <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <RechartsTooltip content={renderTooltip} />
          <Area
            type="monotone"
            dataKey="avg"
            stroke={theme.palette.primary.main}
            fill="url(#analyticsBitrateGradient)"
            fillOpacity={1}
            strokeWidth={2}
            isAnimationActive={true}
            name="avg"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default StreamBitrateAnalyticsChart;
